import debounce from 'lodash/debounce'
import useTranslation from 'next-translate/useTranslation'
import { FocusEvent, use, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { v4 as uuidv4 } from 'uuid'

import {
  GetAddressFromAutocompleteQuery,
  GetAddressFromAutocompleteQueryVariables,
  useGetAddressFromAutocompleteQuery,
} from '@unreserved-frontend-v2/api/generated/graphql/get-address-from-autocomplete.graphql'

import { Address, AddressSuggestion } from '@unreserved-frontend-v2/api/generated/graphql/types'
import { useGetGeosearchResults } from '@unreserved-frontend-v2/api/hooks/use-get-geosearch-results'
import { getSimpleAddress } from '@unreserved-frontend-v2/geolocation/utils/utils'
import { AddressSearchContent } from '@unreserved-frontend-v2/modules/address-selector/components/address-search/address-search-content'
import { BaseStringInput } from '@unreserved-frontend-v2/ui/base-string-input/base-string-input'
import { useClickOutside } from '@unreserved-frontend-v2/ui/hooks/use-click-outside/use-click-outside'
import { UseQueryDataHookType, useQueryData } from '@unreserved-frontend-v2/api/hooks/use-query-data'
import { ClientError } from 'graphql-request'

const ADDRESS_LOOKUP_DEBOUNCE = 300

export interface AddressSearchLiteProps {
  containerClassName?: string

  /** classes to override on the search input */
  inputClassName?: string

  inputPlaceholder?: string

  /** current address to pre-populate the search term */
  address?: Address

  /** callback invoked when search status changes */
  onSearch?: (isSearching: boolean) => void

  /** callback invoked with address that's selected */
  onAddressSelected: (address?: Address) => void

  onInputBlur?: (event: FocusEvent) => void
}

/**
 * Basic AddressSearch component that simply gets an address and adds it to the input
 */
export const AddressSearchLite = ({
  onAddressSelected,
  containerClassName,
  inputClassName,
  inputPlaceholder,
  address,
  onSearch,
  onInputBlur,
}: AddressSearchLiteProps) => {
  const { t } = useTranslation()
  const addressRef = useRef<HTMLDivElement>(null)
  const [sessionToken, setSessionToken] = useState(uuidv4())
  const [addressSuggestion, setAddressSuggestion] = useState<AddressSuggestion | null>(null)
  const [addressLookupEnabled, setAddressLookupEnabled] = useState<boolean>(false)

  /** value of the controlled search input */
  const [searchInputValue, setSearchInputValue] = useState<string>(address ? getSimpleAddress(address) : '')

  /** api input value used to look up the address */
  const [inputValue, setInputValue] = useState('')

  /** whether the search is active to show the list */
  const [isActive, setIsActive] = useState(false)

  const {
    data: addressLookupData,
    isSuccess: addressLookupSucceeded,
    isError: addressLookupFailed,
  } = useQueryData<ClientError, GetAddressFromAutocompleteQuery, GetAddressFromAutocompleteQueryVariables>(
    useGetAddressFromAutocompleteQuery as UseQueryDataHookType,
    { input: { placeId: addressSuggestion?.placeId || '', sessionToken } },
    { enabled: addressLookupEnabled }
  )

  // Address lookup succeeded.
  useEffect(() => {
    if (addressLookupEnabled && addressLookupSucceeded) {
      setAddressLookupEnabled(false)
      setSearchInputValue(getSimpleAddress(addressLookupData?.addressFromAutocomplete as Address))
      onAddressSelected(addressLookupData?.addressFromAutocomplete as Address)
      onSearch?.(false)

      // We've done a complete address lookup/select session so start a new session here.
      setSessionToken(uuidv4())
    }
  }, [addressLookupData, addressLookupSucceeded, addressLookupEnabled, onAddressSelected, onSearch])

  // Address lookup failed.
  useEffect(() => {
    setAddressLookupEnabled(false)
    onSearch?.(false)
  }, [addressLookupFailed, onSearch])

  const handleAddressSelected = useCallback(
    (addressSuggestion: AddressSuggestion) => {
      onSearch?.(true)
      setIsActive(false)
      setAddressLookupEnabled(true)
      setAddressSuggestion(addressSuggestion)
    },
    [onSearch]
  )

  const handleOptionEnterKey = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>, address: AddressSuggestion) => {
      if (event.code === 'Enter') {
        handleAddressSelected(address)
      }
    },
    [handleAddressSelected]
  )

  const searchEnabled = !!inputValue && inputValue.length > 2
  const {
    addresses: addressData,
    isRefetching: isAddressLoading,
    isError: isAddressError,
  } = useGetGeosearchResults(inputValue, sessionToken, {
    enabled: searchEnabled,
  })

  useClickOutside(addressRef, () => {
    setIsActive(false)
  })

  const handleActive = useCallback(() => {
    setIsActive(true)
  }, [])

  const handleDebouncedValueChange = useMemo(() => {
    return debounce((value: string) => {
      setInputValue(value)
      setIsActive(true)
    }, ADDRESS_LOOKUP_DEBOUNCE)
  }, [])

  return (
    <div ref={addressRef} className={containerClassName}>
      <BaseStringInput
        id={'address'}
        name={'address'}
        className={inputClassName}
        required={true}
        placeholder={inputPlaceholder || t('geolocation:address.enter-address')}
        autoComplete="off"
        value={searchInputValue}
        onClick={handleActive}
        onBlur={(event) => {
          if (!isActive) {
            onInputBlur?.(event)
          }
        }}
        // required prop but not actually used, overriden below
        onValueChange={() => {}}
        // overriding onChange to make this a controller input so that we can set the search
        // input value once it is selected from the list
        onChange={(event) => {
          // set one value to display in the search input with immediate feedback
          setSearchInputValue(event.target.value)

          // debounce the actual api search
          handleDebouncedValueChange(event.target.value)
        }}
      />
      {addressData || isAddressLoading || isAddressError ? (
        <div
          className={twMerge(
            'absolute left-0 right-0 top-12 z-10 flex flex-col rounded border border-shades-200 bg-white drop-shadow-md',
            isActive ? 'block' : 'hidden'
          )}
        >
          <AddressSearchContent
            isMobile={false}
            results={(addressData as AddressSuggestion[]) || []}
            isSearchLoading={isAddressLoading}
            isSearchError={isAddressError}
            searchEnabled={searchEnabled}
            handleSelected={handleAddressSelected}
            handleOptionEnterKey={handleOptionEnterKey}
          />
        </div>
      ) : null}
    </div>
  )
}
